import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { SpreadButton } from '@anytag/shared-molecules';
import { useSelectItemIds, useTranslateCountry } from '@anytag/shared-hooks';
import { AUTO_MANAGED_CAMPAIGN_TYPE_OPTIONS } from '@anytag/shared-constants';
import { formatIntNumber, formatNumber, formatPercent } from '@anytag/shared-utils';
import { CheckBox, TableCell } from '@anytag/shared-atoms';
import { Badge, BadgeProps } from '@anyui/core';
import {
  AutoManagedCampaignStatus,
  AutoManagedCampaignInfoPayload,
  GetAutoManagedCampaignsSortField
} from '@anytag/graphql';

type Props = Pick<
  ReturnType<typeof useSelectItemIds<number>>,
  'selectItem' | 'selectedItemIds' | 'hasAllItemsSelected' | 'toggleSelectAllItems'
>;

export const useAutoManagedCampaignsTable = ({
  selectItem,
  selectedItemIds,
  hasAllItemsSelected,
  toggleSelectAllItems
}: Props) => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();

  const getStatusColumnProps = (status: AutoManagedCampaignStatus): BadgeProps => {
    switch (status) {
      case AutoManagedCampaignStatus.ACTIVE:
        return { children: t('Option.Active'), color: 'green' };
      case AutoManagedCampaignStatus.INACTIVE:
        return { children: t('Option.Inactive'), color: 'zinc' };
      case AutoManagedCampaignStatus.FINISHED:
        return { children: t('Option.Finished'), color: 'zinc' };
      default:
        return { children: null, color: 'zinc' };
    }
  };

  const columns: ColumnDef<AutoManagedCampaignInfoPayload>[] = [
    {
      accessorKey: 'title',
      meta: { sticky: true },
      header: () => (
        <div className="flex items-center">
          <CheckBox onClick={toggleSelectAllItems} help={t(hasAllItemsSelected ? 'Unselect all' : 'Select all')} />
          <span className="ml-2">{t('HeaderColumn.Campaign Title')}</span>
        </div>
      ),
      cell: ({ row }) => {
        const isSelected = selectedItemIds.includes(row.original.id);

        return (
          <div className="campaign-title-column">
            <CheckBox
              checked={isSelected}
              onClick={() => selectItem(row.original.id)}
              help={t(isSelected ? 'Unselect' : 'Select')}
            />
            <TableCell.Link
              lines={1}
              variant="blue"
              to="/auto-managed/$id"
              className="ml-2 flex-1"
              label={row.original.title}
              params={{ id: row.original.id.toString() }}
            />
            <SpreadButton
              options={[
                {
                  icon: 'chart',
                  label: t('Report'),
                  tooltip: { help: t('Button.Report') },
                  to: '/auto-managed/$id/report',
                  params: { id: row.original.id.toString() }
                }
              ]}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'autoManagedCampaignType',
      header: t<string>('HeaderColumn.Marketplace'),
      cell: ({ row }) => (
        <TableCell.Text
          value={
            AUTO_MANAGED_CAMPAIGN_TYPE_OPTIONS.find((opt) => opt.value === row.original.autoManagedCampaignType)
              ?.label || '-'
          }
        />
      )
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      cell: ({ row }) => <TableCell.Text value={tc(row.original.country.name)} className="min-w-30" />
    },
    {
      accessorKey: 'status',
      meta: { align: 'center' },
      header: t<string>('HeaderColumn.Status'),
      cell: ({ row }) => <Badge {...getStatusColumnProps(row.original.status)} />
    },
    {
      accessorKey: 'sellerName',
      header: t<string>('HeaderColumn.Seller Name'),
      cell: ({ row }) => <TableCell.Text value={row.original.sellerName} className="min-w-30" />
    },
    {
      accessorKey: 'numberOfJoinedCreators',
      header: t<string>('HeaderColumn.Joined Influencers'),
      meta: { align: 'right', sortField: GetAutoManagedCampaignsSortField.JOINED_CREATORS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.numberOfJoinedCreators)} />
    },
    {
      accessorKey: 'orders',
      header: t<string>('HeaderColumn.Orders'),
      meta: { align: 'right', sortField: GetAutoManagedCampaignsSortField.ORDERS },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.orders)} />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Sales'),
      meta: { align: 'right', sortField: GetAutoManagedCampaignsSortField.SALES },
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.sales.amount)} unit={t(row.original.sales.currencyId)} />
      )
    },
    {
      accessorKey: 'commissionRate',
      header: t<string>('HeaderColumn.Commission Rate'),
      meta: { align: 'right', sortField: GetAutoManagedCampaignsSortField.COMMISSION_RATE },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.commissionRate, false)} unit="%" />
    }
  ];

  return { columns };
};
